import React, { useEffect, useState } from 'react';
import { observer, useLocalObservable } from 'mobx-react-lite';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    Typography,
    Pagination,
    Autocomplete,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DocumentHistoryDto } from '../../../types/document';
import HistoryDialogRow from './HistoryDialogRow';
import useStore from '../../../store/storeContext';
import ShvkTextField from '../../../styled/ShvkTextField';

const PREFIX = 'HistoryDialog';

const classes = {
    paper: `${PREFIX}-paper`,
};

const StyledDialog = styled(Dialog)({
    [`& .${classes.paper}`]: {
        minHeight: '450px',
    },
});

interface Props {
    isOpen: boolean;
    close(): void;
}

interface StoreState {
    page: number;
    historyDates: string[];
    history: DocumentHistoryDto[];
    pageCount: number;
    setPage: (page: number) => void;
}

const itemsPerPage: Readonly<number> = 5;

function HistoryDialog(props: Props) {
    const { document, localization, snackbar, loadingIndicator } = useStore();

    const [date, setDate] = useState<string>('');

    const localStore = useLocalObservable<StoreState>(() => ({
        page: 1,

        get historyDates(): string[] {
            return document.documentHistoryDates;
        },

        get history(): DocumentHistoryDto[] {
            const pageFirstIndex = (this.page - 1) * itemsPerPage;
            return document.documentHistory.slice(pageFirstIndex, pageFirstIndex + itemsPerPage);
        },

        get pageCount(): number {
            return Math.ceil(document.documentHistory.length / itemsPerPage);
        },

        setPage(page: number) {
            this.page = page;
        },
    }));

    useEffect(() => {
        props.isOpen && void getData();
        return () => localStore.setPage(1);
    }, [props.isOpen]);

    async function getData(): Promise<void> {
        try {
            loadingIndicator.show();
            await document.fetchHistoryDates();
            if (document.documentHistoryDates.length > 0) {
                setDate(document.documentHistoryDates[0]);
                await document.fetchHistoryOfDocument(document.documentHistoryDates[0]);
            } else {
                setDate('');
            }
        } catch (e) {
            snackbar.showFetchFailedMessage(e.data?.code);
        } finally {
            loadingIndicator.hide();
        }
    }

    function handlePageChange(_event: React.ChangeEvent<unknown>, value: number): void {
        localStore.setPage(value);
    }

    const { translate } = localization;

    return (
        <StyledDialog
            open={props.isOpen}
            onClose={props.close}
            aria-labelledby="document-history-dialog-title"
            maxWidth={'sm'}
            fullWidth
            classes={{
                paper: classes.paper,
            }}
        >
            <DialogTitle id="document-history-dialog-title">{translate('EDITING_HISTORY')}</DialogTitle>
            <DialogContent dividers>
                <Typography sx={{ mb: 2 }}>{translate('EDITING_HISTORY_SUBTITLE')}</Typography>
                <Autocomplete
                    disablePortal
                    id="history-dates"
                    options={localStore.historyDates}
                    value={date}
                    disableClearable
                    onChange={async (_event, newValue) => {
                        setDate(newValue);
                        localStore.setPage(1);
                        try {
                            await document.fetchHistoryOfDocument(newValue);
                        } catch (e) {
                            snackbar.showFetchFailedMessage(e.data?.code);
                        }
                    }}
                    sx={{ width: 300 }}
                    renderInput={(params) => <ShvkTextField {...params} label={translate('EDITING_DATE')} />}
                />
                <Table>
                    <TableBody>
                        {localStore.history.map((historyTitle) => (
                            <HistoryDialogRow historyTitle={historyTitle} key={historyTitle.id} />
                        ))}
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={props.close}>
                    {translate('CANCEL')}
                </Button>
                <Box flexGrow={1} />
                <Pagination
                    count={localStore.pageCount}
                    page={localStore.page}
                    onChange={handlePageChange}
                    color="primary"
                />
            </DialogActions>
        </StyledDialog>
    );
}

export default observer(HistoryDialog);
